// third-party
import { flexRender, getCoreRowModel, useReactTable, type Row } from '@tanstack/react-table';
import type { ComponentProps, CSSProperties, ReactNode } from 'react';

// project import
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import type { DataTableColumnDef } from 'components/tables/data-table';
// Side-effect import: brings the `ColumnMeta` augmentation (className / width / textAlign / …)
// into scope so `columnDef.meta` reads below are typed rather than cast.
import 'components/tables/data-table/columnMeta';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useIsMobile } from 'hooks/useIsMobile';
import { cn } from 'utils/cn';

/** Width preferences a column inherits when it declares none, written to the header cell's inline
 *  style. Under the auto table layout a `width` is only a hint the content can override, and
 *  `min-width: 0` sets no floor, so an undeclared column stays content-driven while still stating
 *  the same preference as its peers. */
const DEFAULT_COLUMN_WIDTH = 150;
const DEFAULT_COLUMN_MIN_WIDTH = 0;

/**
 * The cells of the app's simple tables, on the kit's table parts: 14px text on a 22px line with
 * tabular figures, 16px of padding (8px in a `small` table), the row's first and last cell always 16px
 * from the edge, and a divider under every row but the last. Content wraps. The divider is drawn by
 * the cell rather than the row so a sticky header, whose table is `border-collapse: separate`, keeps it.
 */
export const SIMPLE_TABLE_CELL_CLASS =
    'border-border text-body1 border-b p-4 whitespace-normal tabular-nums first:pl-4 last:pr-4 [&:has([role=checkbox])]:pr-4 [tr:last-child>&]:border-b-0';

/** The header cells of the app's simple tables: 12px medium text over the grey header band. */
export const SIMPLE_TABLE_HEAD_CLASS =
    'border-border bg-grey-100 h-auto border-b px-4 pt-2 pb-[9px] text-xs leading-5 font-medium whitespace-normal first:pl-4 last:pr-4 [&:has([role=checkbox])]:pr-4';

/** The rows of the app's simple tables carry no hover or divider of their own; the cells draw the divider. */
export const SIMPLE_TABLE_ROW_CLASS = 'border-b-0 hover:bg-transparent';

interface BasicTableProps<T extends object> {
    columns: DataTableColumnDef<T>[];
    data: T[];
    striped?: boolean;
    skipHeader?: boolean;
    /** `small` halves the cell padding; the first and last cell keep 16px. */
    size?: 'small' | 'medium';
    /** Pins the header row while the container scrolls; give the container a max height to scroll. */
    stickyHeader?: boolean;
    className?: string;
    /** Classes for the scrolling box around the `<table>`, e.g. a max height. */
    containerClassName?: string;
    noContentJSX?: JSX.Element | null;
    getRowStyle?: (row: Row<T>) => CSSProperties | undefined;
    getRowClassName?: (row: Row<T>) => string | undefined;
    /** What a row stands as instead of its cells, drawn in one cell across every column; undefined keeps the cells. */
    renderWholeRow?: (row: Row<T>) => ReactNode;
}

export function ReactTable<T extends object>({
    columns,
    data,
    striped,
    skipHeader = false,
    size = 'medium',
    stickyHeader = false,
    className,
    containerClassName,
    noContentJSX = null,
    getRowStyle,
    getRowClassName,
    renderWholeRow,
    ...tableProps
}: BasicTableProps<T> & Omit<ComponentProps<'table'>, keyof BasicTableProps<T>>) {
    // react-hooks-js/incompatible-library: `useReactTable` returns an instance whose methods are
    // functions the React Compiler cannot memoize, so it skips compiling this component. Inherent
    // to the API (not refactorable), and nothing here relies on the compiler — this table renders a
    // handful of rows with no windowing or geometry engine behind it.
    // eslint-disable-next-line react-hooks-js/incompatible-library -- TanStack Table returns non-memoizable functions
    const table = useReactTable<T>({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
    });

    const isMobile = useIsMobile();
    const cellClass = cn(SIMPLE_TABLE_CELL_CLASS, size === 'small' && 'p-2');

    return (
        // The scroll box is the table's own rather than the kit `Table` wrapper, so a caller can bound
        // its height: a sticky header sticks to the nearest scrolling ancestor, and it has to be this one.
        <div className={cn('relative w-full overflow-auto', containerClassName)}>
            <table
                className={cn(
                    'w-full text-left text-sm',
                    // Preflight collapses table borders; a sticky header needs them separate, or the
                    // divider under it scrolls away with the body.
                    stickyHeader && 'border-separate border-spacing-0',
                    className
                )}
                {...tableProps}
            >
                {!skipHeader && (
                    <TableHeader className="[&_tr]:border-b-0">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className={SIMPLE_TABLE_ROW_CLASS}>
                                {headerGroup.headers.map((header, index) => {
                                    const meta = header.column.columnDef.meta;
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={cn(
                                                SIMPLE_TABLE_HEAD_CLASS,
                                                stickyHeader && 'sticky top-0 z-2',
                                                meta?.className
                                            )}
                                            style={{
                                                textAlign: meta?.textAlign,
                                                minWidth:
                                                    index === 0 && !isMobile
                                                        ? 'auto'
                                                        : (meta?.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH),
                                                width: meta?.width ?? DEFAULT_COLUMN_WIDTH,
                                            }}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                )}
                <TableBody>
                    {table.getRowModel().rows.length > 0
                        ? table.getRowModel().rows.map((row) => {
                              const wholeRow = renderWholeRow?.(row);
                              return (
                                  <TableRow
                                      key={row.id}
                                      className={cn(
                                          SIMPLE_TABLE_ROW_CLASS,
                                          striped && 'even:bg-grey-50 even:hover:bg-grey-50',
                                          getRowClassName?.(row)
                                      )}
                                      style={getRowStyle?.(row)}
                                  >
                                      {wholeRow !== undefined ? (
                                          <TableCell colSpan={columns.length} className={cellClass}>
                                              {wholeRow}
                                          </TableCell>
                                      ) : (
                                          row.getVisibleCells().map((cell) => (
                                              <TableCell
                                                  key={cell.id}
                                                  className={cn(cellClass, cell.column.columnDef.meta?.className)}
                                              >
                                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                              </TableCell>
                                          ))
                                      )}
                                  </TableRow>
                              );
                          })
                        : noContentJSX && (
                              <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                  <TableCell colSpan={columns.length} className={cellClass}>
                                      {noContentJSX}
                                  </TableCell>
                              </TableRow>
                          )}
                </TableBody>
            </table>
        </div>
    );
}

interface Props<T extends object> extends BasicTableProps<T> {
    title?: string;
    /** Classes for the card around the table. */
    wrapperClassName?: string;
}

const BasicTable = <T extends object>({ title, wrapperClassName, ...tableProps }: Props<T>) => {
    return (
        <MainCard content={false} title={title} className={wrapperClassName}>
            <ScrollX>
                <ReactTable<T> {...tableProps} />
            </ScrollX>
        </MainCard>
    );
};

export default BasicTable;
